import { useEffect, useMemo } from 'react';
import { Button, Drawer, Form as AntForm, Input, Space, Tag as AntTag } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { tagsApi } from '@api/tags';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { Translator } from '@/i18n/config';
import { recordAuditAction } from '@features/audit/recordAction';
import type { Tag } from '../types';
import { TAG_COLOR_OPTIONS } from '../types';

const SLUG_RULE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function makeSchema(t: Translator) {
  return z.object({
    name: z.string().min(2, t('validation.nameMin')).max(40),
    slug: z
      .string()
      .min(2)
      .max(40)
      .regex(SLUG_RULE, t('validation.slugFormat')),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, t('validation.colorHex')),
    description: z.string().max(200).optional(),
  });
}
type Values = z.infer<ReturnType<typeof makeSchema>>;

interface Props {
  open: boolean;
  tag: Tag | null;
  onClose: () => void;
}

function ColorSwatchPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation('tags');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      {TAG_COLOR_OPTIONS.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={t('form.colorAriaLabel', { color: c })}
          onClick={() => onChange(c)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: c,
            border:
              value.toLowerCase() === c.toLowerCase()
                ? '2px solid var(--ds-text-primary)'
                : '2px solid transparent',
            outline: 'none',
            cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          }}
        />
      ))}
      <Input
        size="small"
        style={{ width: 110 }}
        value={value}
        maxLength={7}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
      />
    </div>
  );
}

export function TagDrawer({ open, tag, onClose }: Props) {
  const { t } = useTranslation(['tags', 'common', 'validation', 'errors']);
  const isEdit = !!tag;
  const qc = useQueryClient();
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);

  const initialValues = useMemo<Values>(
    () =>
      tag
        ? {
            name: tag.name,
            slug: tag.slug,
            color: tag.color,
            description: tag.description ?? '',
          }
        : { name: '', slug: '', color: TAG_COLOR_OPTIONS[0], description: '' },
    [tag],
  );

  const createMutation = useMutation({
    mutationFn: (v: Values) =>
      tagsApi.create({
        name: v.name,
        slug: v.slug,
        color: v.color,
        description: v.description || undefined,
      }),
    onSuccess: (created) => {
      toast.success(t('toast.created', { name: created.name }));
      void recordAuditAction({
        action: 'tag.create',
        resource: 'tag',
        resourceId: created.id,
        summary: t('audit.create', { name: created.name }),
      });
      void qc.invalidateQueries({ queryKey: ['tags'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (v: Values) => {
      if (!tag) throw new Error('no tag');
      return tagsApi.update(tag.id, {
        name: v.name,
        slug: v.slug,
        color: v.color,
        description: v.description || undefined,
      });
    },
    onSuccess: (updated) => {
      toast.success(t('toast.updated', { name: updated.name }));
      void recordAuditAction({
        action: 'tag.update',
        resource: 'tag',
        resourceId: updated.id,
        summary: t('audit.update', { name: updated.name }),
      });
      void qc.invalidateQueries({ queryKey: ['tags'] });
      onClose();
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  useEffect(() => {
    if (!open) {
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Drawer
      title={isEdit ? t('drawer.editTitle', { name: tag?.name }) : t('drawer.createTitle')}
      open={open}
      onClose={onClose}
      width={Math.min(480, typeof window !== 'undefined' ? window.innerWidth : 480)}
      destroyOnClose
    >
      <Form
        schema={schema}
        defaultValues={initialValues}
        onSubmit={(v) => (isEdit ? updateMutation.mutate(v) : createMutation.mutate(v))}
      >
        {({ watch, setValue }) => {
          const name = watch('name');
          const slug = watch('slug');
          const color = watch('color');
          return (
            <>
              <div
                style={{
                  marginBottom: 16,
                  padding: 12,
                  background: 'var(--ds-surface-2)',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                  {t('drawer.preview')}
                </span>
                <AntTag color={color} style={{ margin: 0 }}>
                  {name || t('drawer.previewSample')}
                </AntTag>
              </div>
              <AntForm layout="vertical" component="div">
                <FormField name="name" label={t('form.labels.name')} required>
                  <Input size="large" placeholder={t('form.placeholders.name')} />
                </FormField>
                <AntForm.Item label={t('form.labels.slug')} required>
                  <SlugInput
                    value={slug}
                    onChange={(v) => setValue('slug', v, { shouldDirty: true, shouldValidate: true })}
                    sourceValue={name}
                  />
                </AntForm.Item>
                <AntForm.Item label={t('form.labels.color')} required>
                  <ColorSwatchPicker
                    value={color}
                    onChange={(v) => setValue('color', v, { shouldDirty: true, shouldValidate: true })}
                  />
                </AntForm.Item>
                <FormField name="description" label={t('form.labels.description')}>
                  <Input.TextArea rows={2} maxLength={200} showCount />
                </FormField>
              </AntForm>
              <Space style={{ marginTop: 12 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createMutation.isPending || updateMutation.isPending}
                >
                  {isEdit ? t('common:actions.save') : t('common:actions.create')}
                </Button>
                <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
              </Space>
            </>
          );
        }}
      </Form>
    </Drawer>
  );
}
